import { usePageHeader } from "contexts/PageHeaderContext";
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription } from '../../ui/alert';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import {
  RefreshCw,
  Building2,
  CheckCircle2,
  XCircle,
  Clock,
  User,
  FileText,
  AlertCircle,
  Upload,
  ChevronDown,
  ChevronUp,
  Eye,
  History,
  Users,
  Trash2
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';

interface RenewalApprovalDetailsPageProps {
  onNavigate: (page: string) => void;
}


interface DocumentItem {
  id: string;
  name: string;
  status: string;
  uploadedUrl: string;
  uploadedAt: string;
}

export default function RenewalApprovalDetailsPage({ onNavigate }: RenewalApprovalDetailsPageProps) {
  const { setPageHeader } = usePageHeader();
  useEffect(() => {
    setPageHeader({
      title: "Renewal Request Details",
      childTitle: "Review and approve tour operator renewal request",
      icon: <RefreshCw className="h-5 w-5" />,
      onBack: () => onNavigate('/registration/to-renewal-list'),
    });
    return () => setPageHeader(null);
  }, [setPageHeader, onNavigate]);

  const [showUploadHistory, setShowUploadHistory] = useState(false);
  const [expandedPartners, setExpandedPartners] = useState<{ [key: string]: boolean }>({});
  const [selectedDoc, setSelectedDoc] = useState<DocumentItem | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [rejectRemark, setRejectRemark] = useState('');

  // Editable renewal info state
  const [renewalInfo, setRenewalInfo] = useState({
    currentExpiryDate: '2025-02-28',
    requestedDate: '2024-12-01',
    daysUntilExpiry: 75
  });

  const togglePartnerExpansion = (partnerId: string) => {
    setExpandedPartners(prev => ({
      ...prev,
      [partnerId]: !prev[partnerId]
    }));
  };

  const handleRejectDocument = () => {
    if (!rejectRemark.trim()) {
      alert('Please add a remark for rejection');
      return;
    }
    alert(`Document "${selectedDoc?.name}" rejected with remark: ${rejectRemark}`);
    setRejectRemark('');
    setIsRejectDialogOpen(false);
    setSelectedDoc(null);
  };

  // Mock renewal data
  const renewal = {
    id: '1',
    tourOperatorName: 'Fatima Tours & Travels',
    officeName: 'Mumbai Main Office',
    itsNumber: '12345678',
    ownerName: 'Fatima Hussain',
    fatherName: 'Ahmed Hussain',
    dateOfBirth: '1985-05-15',
    mobileNumber: '+91 98765 43210',
    whatsappNumber: '+91 98765 43210',
    email: 'fatima@example.com',
    officeAddress: '123, Business Complex, Andheri West, Mumbai - 400058',
    permanentAddress: '456, Residential Area, Bandra, Mumbai - 400050',
    businessName: 'Fatima Tours & Travels',
    businessType: 'Sole Proprietorship',
    passportNumber: 'A1234567',
    currentExpiryDate: '2025-02-28',
    requestedDate: '2024-12-01',
    status: 'pending',
    daysUntilExpiry: 75
  };

  // Mock upload history
  const uploadHistory = [
    {
      id: '1',
      status: 'rejected',
      uploadedAt: '2025-11-21T11:00:00',
      reviewedBy: 'Super Admin',
      reviewedAt: '2025-11-21T16:30:00',
      remarks: 'Incomplete information in the approval document',
      fileUrl: '#'
    },
    {
      id: '2',
      status: 'pending',
      uploadedAt: '2025-11-23T14:45:00',
      fileUrl: '#'
    }
  ];

  // Mock TO documents
  const toDocuments = [
    { id: 'doc1', name: 'Passport Copy', status: 'uploaded', uploadedUrl: 'https://example.com/doc1.pdf', uploadedAt: '2024-11-20' },
    { id: 'doc2', name: 'ITS Card', status: 'uploaded', uploadedUrl: 'https://example.com/doc2.pdf', uploadedAt: '2024-11-20' },
    { id: 'doc3', name: 'Business Registration Certificate', status: 'uploaded', uploadedUrl: 'https://example.com/doc3.pdf', uploadedAt: '2024-11-20' },
    { id: 'doc4', name: 'Office Address Proof', status: 'uploaded', uploadedUrl: 'https://example.com/doc4.pdf', uploadedAt: '2024-11-21' },
    { id: 'doc5', name: 'Bank Account Statement', status: 'uploaded', uploadedUrl: 'https://example.com/doc5.pdf', uploadedAt: '2024-11-21' },
  ];

  // Mock partners/members data
  const partners = [
    {
      id: 'p1',
      name: 'Ahmed Khan',
      itsNumber: '87654321',
      memberType: 'Partner',
      documents: [
        { id: 'p1-doc1', name: 'Passport Copy', status: 'uploaded', uploadedUrl: 'https://example.com/p1-doc1.pdf', uploadedAt: '2024-11-22' },
        { id: 'p1-doc2', name: 'ITS Card', status: 'uploaded', uploadedUrl: 'https://example.com/p1-doc2.pdf', uploadedAt: '2024-11-22' },
        { id: 'p1-doc3', name: 'Photograph', status: 'uploaded', uploadedUrl: 'https://example.com/p1-doc3.pdf', uploadedAt: '2024-11-22' },
      ]
    },
    {
      id: 'p2',
      name: 'Zainab Malik',
      itsNumber: '11223344',
      memberType: 'Manager',
      documents: [
        { id: 'p2-doc1', name: 'Passport Copy', status: 'uploaded', uploadedUrl: 'https://example.com/p2-doc1.pdf', uploadedAt: '2024-11-23' },
        { id: 'p2-doc2', name: 'ITS Card', status: 'uploaded', uploadedUrl: 'https://example.com/p2-doc2.pdf', uploadedAt: '2024-11-23' },
        { id: 'p2-doc3', name: 'Photograph', status: 'uploaded', uploadedUrl: 'https://example.com/p2-doc3.pdf', uploadedAt: '2024-11-23' },
      ]
    },
    {
      id: 'p3',
      name: 'Hassan Ali',
      itsNumber: '55667788',
      memberType: 'Partner',
      documents: [
        { id: 'p3-doc1', name: 'Passport Copy', status: 'uploaded', uploadedUrl: 'https://example.com/p3-doc1.pdf', uploadedAt: '2024-11-24' },
        { id: 'p3-doc2', name: 'ITS Card', status: 'uploaded', uploadedUrl: 'https://example.com/p3-doc2.pdf', uploadedAt: '2024-11-24' },
        { id: 'p3-doc3', name: 'Photograph', status: 'uploaded', uploadedUrl: 'https://example.com/p3-doc3.pdf', uploadedAt: '2024-11-24' },
      ]
    },
  ];

  const getStatusBadge = () => {
    switch (renewal.status) {
      case 'approved':
        return (
          <Badge className="bg-green-500 text-white">
            <CheckCircle2 className="w-4 h-4 mr-1" />
            Approved
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-orange-500 text-white">
            <Clock className="w-4 h-4 mr-1" />
            Pending Review
          </Badge>
        );
      case 'rejected':
        return (
          <Badge className="bg-red-500 text-white">
            <XCircle className="w-4 h-4 mr-1" />
            Rejected
          </Badge>
        );
      default:
        return null;
    }
  };


  const handleFileUpload = () => {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = ".pdf";

  input.onchange = (e: Event) => {
    const target = e.target;
    if (!(target instanceof HTMLInputElement)) return;

    const file = target.files?.[0];
    if (file) alert(`File selected: ${file.name}`);
  };

  input.click();
}

  return (
    <div className="space-y-4 md:space-y-6">
            {/* Status Badge */}
      <div className="flex items-center gap-3">
        {getStatusBadge()}
        <div className="text-sm text-gray-600">
          Requested on {new Date(renewal.requestedDate).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}
        </div>
      </div>

      {/* Alert for pending status */}
      {renewal.status === 'pending' && (
        <Alert className="border-orange-200 bg-orange-50">
          <AlertCircle className="w-4 h-4 text-orange-600" />
          <AlertDescription className="text-orange-800">
            <strong>Action Required:</strong> This renewal request is pending your approval. Review the details below and approve or reject the request.
          </AlertDescription>
        </Alert>
      )}

      {/* Complete Tour Operator Details */}
      <Card className="border-[#5B9BD5]/20 bg-gradient-to-br from-white to-sky-50/30 shadow-lg">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-md" style={{ backgroundColor: '#5B9BD5' }}>
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-gray-900 text-xl">{renewal.tourOperatorName}</CardTitle>
                <p className="text-sm text-gray-600 mt-0.5">Complete Registration Details</p>
              </div>
            </div>
            <Badge variant="outline" className="bg-white text-gray-600 border-gray-300 shadow-sm">
              ITS: {renewal.itsNumber}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">

          {/* Tour Operator Details / Personal Information */}
          <div>
            <h3 className="flex items-center gap-2 mb-4 pb-2 border-b-2" style={{ borderColor: '#5B9BD5', color: '#5B9BD5' }}>
              <User className="w-5 h-5" />
              <span className="font-semibold">Tour Operator Details</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-1">
                <p className="text-xs text-gray-600">Full Name</p>
                <p className="text-sm font-medium text-gray-900">{renewal.ownerName}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-gray-600">Father's Name</p>
                <p className="text-sm font-medium text-gray-900">{renewal.fatherName}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-gray-600">Date of Birth</p>
                <p className="text-sm font-medium text-gray-900">
                  {new Date(renewal.dateOfBirth).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-gray-600">Passport Number</p>
                <p className="text-sm font-medium text-gray-900 font-mono">{renewal.passportNumber}</p>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="flex items-center gap-2 mb-4 pb-2 border-b-2" style={{ borderColor: '#5B9BD5', color: '#5B9BD5' }}>
              <Building2 className="w-5 h-5" />
              <span className="font-semibold">Contact Information</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-1">
                <p className="text-xs text-gray-600">Mobile Number</p>
                <p className="text-sm font-medium text-gray-900">{renewal.mobileNumber}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-gray-600">WhatsApp Number</p>
                <p className="text-sm font-medium text-gray-900">{renewal.whatsappNumber}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-gray-600">Email Address</p>
                <p className="text-sm font-medium text-gray-900 break-all">{renewal.email}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-gray-600">Office Name</p>
                <p className="text-sm font-medium text-gray-900">{renewal.officeName}</p>
              </div>
            </div>
          </div>

          {/* Business Information */}
          <div>
            <h3 className="flex items-center gap-2 mb-4 pb-2 border-b-2" style={{ borderColor: '#5B9BD5', color: '#5B9BD5' }}>
              <Building2 className="w-5 h-5" />
              <span className="font-semibold">Business Information</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-1">
                <p className="text-xs text-gray-600">Business Name</p>
                <p className="text-sm font-medium text-gray-900">{renewal.businessName}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-gray-600">Business Type</p>
                <p className="text-sm font-medium text-gray-900">{renewal.businessType}</p>
              </div>
              <div className="space-y-1 md:col-span-2 lg:col-span-1">
                <p className="text-xs text-gray-600">Office Address</p>
                <p className="text-sm font-medium text-gray-900">{renewal.officeAddress}</p>
              </div>
              <div className="space-y-1 md:col-span-2 lg:col-span-2">
                <p className="text-xs text-gray-600">Permanent Address</p>
                <p className="text-sm font-medium text-gray-900">{renewal.permanentAddress}</p>
              </div>
            </div>
          </div>

          {/* Renewal Information */}
          <div>
            <h3 className="flex items-center gap-2 mb-4 pb-2 border-b-2" style={{ borderColor: '#5B9BD5', color: '#5B9BD5' }}>
              <RefreshCw className="w-5 h-5" />
              <span className="font-semibold">Renewal Information</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-xs text-gray-600">Current Expiry Date</Label>
                <Input
                  type="date"
                  value={renewalInfo.currentExpiryDate}
                  onChange={(e) => setRenewalInfo({ ...renewalInfo, currentExpiryDate: e.target.value })}
                  className="border-gray-300 focus:border-[#5B9BD5] focus:ring-[#5B9BD5] text-sm"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-gray-600">Days Remaining</Label>
                <Input
                  type="number"
                  value={renewalInfo.daysUntilExpiry}
                  onChange={(e) => setRenewalInfo({ ...renewalInfo, daysUntilExpiry: parseInt(e.target.value) })}
                  className="border-gray-300 focus:border-[#5B9BD5] focus:ring-[#5B9BD5] text-sm"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-gray-600">Request Submitted</Label>
                <p className="text-sm font-medium text-gray-900 py-2">
                  {new Date(renewalInfo.requestedDate).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-gray-600">Current Status</p>
                {getStatusBadge()}
              </div>
            </div>
          </div>

        </CardContent>
      </Card>

      {/* Tour Operator Documents Section */}
      <Card className="border-[#5B9BD5]/20 bg-white shadow-lg">
        <CardHeader>
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5" style={{ color: '#5B9BD5' }} />
            <CardTitle style={{ color: '#5B9BD5' }}>
              Tour Operator Documents
            </CardTitle>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            All documents uploaded by the tour operator for renewal
          </p>
        </CardHeader>
        <CardContent>
          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-hidden rounded-lg border border-gray-200">
            <table className="w-full">
              <thead>
                <tr style={{ backgroundColor: '#E3F2FD' }}>
                  <th className="px-4 py-3 text-left text-xs" style={{ color: '#5B9BD5' }}>
                    S. No.
                  </th>
                  <th className="px-4 py-3 text-left text-xs" style={{ color: '#5B9BD5' }}>
                    Document Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs" style={{ color: '#5B9BD5' }}>
                    Uploaded Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs" style={{ color: '#5B9BD5' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {toDocuments.map((doc, index) => (
                  <tr key={doc.id} className="hover:bg-sky-50/30">
                    <td className="px-4 py-3">
                      <span className="text-gray-900 text-sm">{index + 1}</span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-gray-900 text-sm">{doc.name}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-gray-900 text-sm">
                        {new Date(doc.uploadedAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedDoc(doc);
                            setIsViewDialogOpen(true);
                          }}
                          className="p-1.5 rounded hover:bg-sky-50 transition-colors"
                          style={{ color: '#5B9BD5' }}
                          title="View Document"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedDoc(doc);
                            setIsRejectDialogOpen(true);
                          }}
                          className="p-1.5 rounded hover:bg-red-50 transition-colors text-red-600"
                          title="Reject Document"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden space-y-3">
            {toDocuments.map((doc, index) => (
              <Card key={doc.id} className="border-gray-200">
                <CardContent className="p-4">
                  <div className="space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 flex-1">
                        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-900 text-sm flex-shrink-0">
                          {index + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 text-sm">{doc.name}</h3>
                          <p className="text-xs text-gray-600 mt-1">
                            {new Date(doc.uploadedAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2 pt-2 border-t">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedDoc(doc);
                          setIsViewDialogOpen(true);
                        }}
                        className="flex-1 text-[#5B9BD5] border-[#5B9BD5] hover:bg-sky-50 text-xs"
                      >
                        <Eye className="w-3.5 h-3.5 mr-1.5" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedDoc(doc);
                          setIsRejectDialogOpen(true);
                        }}
                        className="flex-1 text-red-600 border-red-300 hover:bg-red-50 text-xs"
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Partner/Member Documents Section */}
      <Card className="border-[#5B9BD5]/20 bg-white shadow-lg">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5" style={{ color: '#5B9BD5' }} />
            <CardTitle style={{ color: '#5B9BD5' }}>
              Partner / Member Documents
            </CardTitle>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Documents uploaded by all partners and members
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {partners.map((partner, partnerIndex) => (
            <div key={partner.id} className="space-y-4">
              {/* Member Header */}
              <div
                className="flex items-center justify-between p-4 rounded-xl cursor-pointer transition-colors"
                style={{ backgroundColor: '#E3F2FD' }}
                onClick={() => togglePartnerExpansion(partner.id)}
              >
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white" style={{ color: '#5B9BD5' }}>
                    <Users className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      Member {partnerIndex + 1} Documents
                    </h3>
                    <p className="text-sm text-gray-600">
                      {partner.name} - ITS: {partner.itsNumber} ({partner.memberType})
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className="bg-green-100 text-green-700 border-green-300">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    All Uploaded
                  </Badge>
                  {expandedPartners[partner.id] ? (
                    <ChevronUp className="w-5 h-5" style={{ color: '#5B9BD5' }} />
                  ) : (
                    <ChevronDown className="w-5 h-5" style={{ color: '#5B9BD5' }} />
                  )}
                </div>
              </div>

              {/* Member Documents - Expandable */}
              {expandedPartners[partner.id] && (
                <div className="space-y-3 pl-4">
                  {/* Desktop Table View */}
                  <div className="hidden lg:block overflow-hidden rounded-lg border border-gray-200">
                    <table className="w-full">
                      <thead>
                        <tr style={{ backgroundColor: '#F5F5F5' }}>
                          <th className="px-4 py-3 text-left text-xs" style={{ color: '#5B9BD5' }}>
                            S. No.
                          </th>
                          <th className="px-4 py-3 text-left text-xs" style={{ color: '#5B9BD5' }}>
                            Document Name
                          </th>
                          <th className="px-4 py-3 text-left text-xs" style={{ color: '#5B9BD5' }}>
                            Uploaded Date
                          </th>
                          <th className="px-4 py-3 text-left text-xs" style={{ color: '#5B9BD5' }}>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {partner.documents.map((doc, docIndex) => (
                          <tr key={doc.id} className="hover:bg-sky-50/30">
                            <td className="px-4 py-3">
                              <span className="text-gray-900 text-sm">{docIndex + 1}</span>
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-gray-900 text-sm">{doc.name}</p>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-gray-900 text-sm">
                                {new Date(doc.uploadedAt).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => {
                                    setSelectedDoc(doc);
                                    setIsViewDialogOpen(true);
                                  }}
                                  className="p-1.5 rounded hover:bg-sky-50 transition-colors"
                                  style={{ color: '#5B9BD5' }}
                                  title="View Document"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedDoc(doc);
                                    setIsRejectDialogOpen(true);
                                  }}
                                  className="p-1.5 rounded hover:bg-red-50 transition-colors text-red-600"
                                  title="Reject Document"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="lg:hidden space-y-3">
                    {partner.documents.map((doc, docIndex) => (
                      <Card key={doc.id} className="border-gray-200">
                        <CardContent className="p-4">
                          <div className="space-y-3">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-center gap-3 flex-1">
                                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-900 text-sm flex-shrink-0">
                                  {docIndex + 1}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <h3 className="font-medium text-gray-900 text-sm">{doc.name}</h3>
                                  <p className="text-xs text-gray-600 mt-1">
                                    {new Date(doc.uploadedAt).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric'
                                    })}
                                  </p>
                                </div>
                              </div>
                            </div>

                            <div className="flex gap-2 pt-2 border-t">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedDoc(doc);
                                  setIsViewDialogOpen(true);
                                }}
                                className="flex-1 text-[#5B9BD5] border-[#5B9BD5] hover:bg-sky-50 text-xs"
                              >
                                <Eye className="w-3.5 h-3.5 mr-1.5" />
                                View
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedDoc(doc);
                                  setIsRejectDialogOpen(true);
                                }}
                                className="flex-1 text-red-600 border-red-300 hover:bg-red-50 text-xs"
                              >
                                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                                Reject
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Document Section - Upload Misal Mubarak */}
      <Card className="border-[#5B9BD5]/20">
        <CardHeader>
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5" style={{ color: '#5B9BD5' }} />
            <CardTitle className="text-gray-900">
              Document Section - Misal Mubarak (Approval Document)
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Upload Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              className="border-[#5B9BD5] text-[#5B9BD5] hover:bg-[#5B9BD5]/5"
              // onClick={() => {
              //   // Handle file upload
              //   const input = document.createElement('input');
              //   input.type = 'file';
              //   input.accept = '.pdf';
              //   input.onchange = (e: any) => {
              //     const file = e.target.files[0];
              //     if (file) {
              //       alert(`File selected: ${file.name}`);
              //     }
              //   };
              //   input.click();
              // }}
              onClick={handleFileUpload}
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Misal Mubarak
            </Button>

            <Button
              variant="outline"
              className="border-gray-300"
              onClick={() => setShowUploadHistory(!showUploadHistory)}
            >
              <History className="w-4 h-4 mr-2" />
              View Upload History ({uploadHistory.length})
              {showUploadHistory ? (
                <ChevronUp className="w-4 h-4 ml-2" />
              ) : (
                <ChevronDown className="w-4 h-4 ml-2" />
              )}
            </Button>
          </div>

          {/* Help Text */}
          <p className="text-sm text-gray-600">
            <span className="text-red-500 font-semibold">* Required:</span> Admin must upload Misal Mubarak before approval. Accepted format: PDF (Max 5MB)
          </p>

          {/* Upload History */}
          {showUploadHistory && (
            <div className="border border-[#5B9BD5]/20 rounded-lg p-4 bg-[#5B9BD5]/5 space-y-4">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4" style={{ color: '#5B9BD5' }} />
                <h4 className="text-sm font-semibold text-gray-900">
                  Misal Mubarak Upload History ({uploadHistory.length} versions)
                </h4>
              </div>

              <div className="space-y-3">
                {uploadHistory.map((upload) => (
                  <div
                    key={upload.id}
                    className={`p-4 rounded-lg border ${upload.status === 'rejected'
                        ? 'bg-red-50 border-red-200'
                        : 'bg-white border-gray-200'
                      }`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge
                            className={`text-xs ${upload.status === 'rejected'
                                ? 'bg-red-600 text-white'
                                : 'bg-gray-500 text-white'
                              }`}
                          >
                            {upload.status === 'rejected' ? 'REJECTED' : 'PENDING'}
                          </Badge>
                          <span className="text-xs text-gray-600">
                            Uploaded: {new Date(upload.uploadedAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit'
                            })} at {new Date(upload.uploadedAt).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true
                            })}
                          </span>
                          {upload.reviewedBy && upload.reviewedAt && (
                            <span className="text-xs text-gray-600">
                              • Reviewed by {upload.reviewedBy} on {new Date(upload.reviewedAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit'
                              })} at {new Date(upload.reviewedAt).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true
                              })}
                            </span>
                          )}
                        </div>
                        {upload.remarks && (
                          <div className="text-sm">
                            <span className="font-semibold text-gray-700">Remarks:</span>{' '}
                            <span className="text-gray-900">{upload.remarks}</span>
                          </div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-[#5B9BD5] text-[#5B9BD5] hover:bg-[#5B9BD5]/5 flex-shrink-0"
                        onClick={() => {
                          window.open(upload.fileUrl, '_blank');
                        }}
                      >
                        <Eye className="w-3.5 h-3.5 mr-1" />
                        View
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Document Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle style={{ color: '#5B9BD5' }}>View Document</DialogTitle>
            <DialogDescription>
              {selectedDoc?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Direct PDF Preview */}
            <div className="w-full h-[600px] rounded-lg overflow-hidden border border-gray-200">
              <iframe
                src={selectedDoc?.uploadedUrl}
                className="w-full h-full"
                title="Document Preview"
              />
            </div>
            <div className="flex justify-end">
              <Button
                variant="outline"
                style={{ borderColor: '#5B9BD5', color: '#5B9BD5' }}
                onClick={() => window.open(selectedDoc?.uploadedUrl, '_blank')}
              >
                <Eye className="w-4 h-4 mr-2" />
                Open in New Tab
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Document Dialog */}
      <Dialog open={isRejectDialogOpen} onOpenChange={setIsRejectDialogOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle style={{ color: '#5B9BD5' }}>Reject Document</DialogTitle>
            <DialogDescription>
              {selectedDoc?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Direct PDF Preview */}
            <div className="w-full h-[400px] rounded-lg overflow-hidden border border-gray-200">
              <iframe
                src={selectedDoc?.uploadedUrl}
                className="w-full h-full"
                title="Document Preview"
              />
            </div>
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                style={{ borderColor: '#5B9BD5', color: '#5B9BD5' }}
                onClick={() => window.open(selectedDoc?.uploadedUrl, '_blank')}
              >
                <Eye className="w-4 h-4 mr-2" />
                Open in New Tab
              </Button>
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-gray-600">Add Remark for Rejection *</Label>
              <Textarea
                value={rejectRemark}
                onChange={(e) => setRejectRemark(e.target.value)}
                className="border-gray-300 focus:border-[#5B9BD5] focus:ring-[#5B9BD5]"
                placeholder="Enter your reason for rejecting this document..."
                rows={4}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              className="border-gray-300 text-gray-600 hover:bg-gray-50"
              onClick={() => {
                setIsRejectDialogOpen(false);
                setRejectRemark('');
              }}
            >
              Cancel
            </Button>
            <Button
              className="text-white"
              style={{ backgroundColor: '#5B9BD5' }}
              onClick={handleRejectDocument}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Reject Document
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Action Buttons */}
      {renewal.status === 'pending' && (
        <div className="flex flex-col sm:flex-row justify-end gap-3">
          <Button
            variant="outline"
            className="border-red-300 text-red-600 hover:bg-red-50"
            onClick={() => {
              // eslint-disable-next-line no-restricted-globals
              if (confirm('Are you sure you want to reject this renewal request?')) {
                alert('Renewal request rejected');
                onNavigate('to-renewal-list');
              }
            }}
          >
            <XCircle className="w-4 h-4 mr-2" />
            Reject Renewal
          </Button>
          <Button
            className="text-white"
            style={{ backgroundColor: '#5B9BD5' }}
            onClick={() => {
              // eslint-disable-next-line no-restricted-globals
              if (confirm('Are you sure you want to approve this renewal request?')) {
                alert('Renewal request approved');
                onNavigate('to-renewal-list');
              }
            }}
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Approve Renewal
          </Button>
        </div>
      )}
    </div>
  );
}
